/**
 * @file adminApi.ts
 * @description RTK Query API slice for the admin panel (admin-role only).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import type { AdminUserListResponse, UserRole, UserPlan } from '@forex-builder/shared-types';
import { createBaseQueryWithReauth } from '../../../shared/api/baseQueryWithReauth.js';

export interface GetAdminUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: UserRole;
  plan?: UserPlan;
}

export interface GetAdminUsersApiResponse {
  success: boolean;
  data: AdminUserListResponse;
}

export interface UpdateUserRoleRequest {
  userId: string;
  role: UserRole;
}

export interface UpdateUserRoleResponse {
  success: boolean;
  message: string;
  data: { id: string; role: UserRole };
}

export interface UpdateUserPlanRequest {
  userId: string;
  plan: UserPlan;
}

export interface UpdateUserPlanResponse {
  success: boolean;
  message: string;
  data: { id: string; plan: UserPlan };
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: createBaseQueryWithReauth('/api/v1/admin'),
  tagTypes: ['AdminUsers'],
  endpoints: (builder) => ({
    getAdminUsers: builder.query<GetAdminUsersApiResponse, GetAdminUsersParams | void>({
      query: (params) => ({
        url: '/users',
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 25,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.role ? { role: params.role } : {}),
          ...(params?.plan ? { plan: params.plan } : {}),
        },
      }),
      providesTags: ['AdminUsers'],
    }),

    updateUserRole: builder.mutation<UpdateUserRoleResponse, UpdateUserRoleRequest>({
      query: ({ userId, role }) => ({
        url: `/users/${userId}/role`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: ['AdminUsers'],
    }),

    updateUserPlan: builder.mutation<UpdateUserPlanResponse, UpdateUserPlanRequest>({
      query: ({ userId, plan }) => ({
        url: `/users/${userId}/plan`,
        method: 'PATCH',
        body: { plan },
      }),
      invalidatesTags: ['AdminUsers'],
    }),
  }),
});

export const { useGetAdminUsersQuery, useUpdateUserRoleMutation, useUpdateUserPlanMutation } = adminApi;
